import { formatInTimeZone } from 'date-fns-tz';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Info, maintenance as maintenanceIcon } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { USER_ROLE, useAuth } from '../../../hooks';
import { useCompany } from '../../../hooks/useCompany';
import { useFetchLocationsQuery } from '../../../services/location.api';
import { Charger, License } from '../../../stores/types';
import { OWNER_TYPE } from '../../../stores/types/sessions.interface';
import { getISO8601TimezoneOffSetFromTimeZone, getLocale } from '../../../utils/Date.Util';
import { ColorType, Dropdown, Icon, Label, LabelType, Switch, getHexColorByType } from '../../_ui';
import { InputType } from '../../_ui/Input.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../_ui/Tooltip.component';
import CopyTooltip from '../../_ui/copyTooltip/CopyTooltip.component';
import { ChargerStatus } from '../ChargerStatus.component';
import ChargersLicenseDetails from '../charger-license/ChargersLicenseDetails.component';
import { OCPP_ID_LENGTH, getRowStatus } from '../utils';
import InputDisplayOCPP from './InputDisplayOCPP.component';

type ChargerInfoProps = {
  charger: Charger;
  license: License | null;
  editEnabled: boolean;
  isLoading: boolean;
  handleInputChange: any;
  manufacturerModelData: any;
  manufacturer: any;
  setManufacturer: any;
  setUpdateChargerDetail: any;
  updateChargerDetail: any;
  setisValidDisplayIdOCPP: any;
};
export const ChargerInfo = ({
  charger,
  license,
  editEnabled,
  isLoading,
  handleInputChange,
  manufacturerModelData,
  manufacturer,
  setManufacturer,
  setUpdateChargerDetail,
  updateChargerDetail,
  setisValidDisplayIdOCPP,
}: ChargerInfoProps) => {
  const { t } = useTranslation();
  const { companies, userScope } = useCompany();
  const auth = useAuth();
  const { locations } = useFetchLocationsQuery(
    {
      scope: userScope!,
      ...(charger.ownerType !== OWNER_TYPE.USER && {
        companyId: charger.ownerId,
      }),
      include: 'all',
    },
    {
      selectFromResult: (endPoint) => ({
        locations: endPoint.data ? endPoint.data.entities : [],
      }),
    },
  );

  const locationDropdown = useMemo(() => {
    return locations.map((location) => ({
      id: location.id,
      label: location.name,
      selected: location.id === charger?.location?.id,
    }));
  }, [locations, charger?.location?.id]);

  const manufacturerDropdown = useMemo(() => {
    return manufacturerModelData.reduce((pre: any, curr: any) => {
      const exist = pre.find((obj: any) => {
        return obj.label === curr.vendor;
      });
      if (!exist) {
        pre.push({
          id: curr.vendor,
          label: curr.vendor,
          selected: curr.vendor === charger?.vendor,
        });
      }
      return pre;
    }, []);
  }, [manufacturerModelData, charger?.vendor]);

  const modelDropdown = useMemo(() => {
    return _.get(
      _.mapValues(
        _.groupBy(
          manufacturerModelData.map((model: any) => ({
            id: model.id,
            label: model.model,
            selected: model.model === charger?.model,
            manufacturer: model.vendor,
          })),
          (element) => element.manufacturer,
        ),
        (list) => list.map((obj) => _.omit(obj, 'manufacturer')),
      ),
      manufacturer,
    );
  }, [manufacturerModelData, manufacturer, charger?.model]);

  useEffect(() => {
    if (manufacturer) return;

    const selectedManufacturer = manufacturerDropdown.find(
      (item: any) => item.selected === true,
    )?.label;

    if (selectedManufacturer) {
      setManufacturer(selectedManufacturer);
    }
  }, [manufacturerDropdown, manufacturer, setManufacturer]);

  const companyName = useMemo(() => {
    return companies?.find((c) => c.id === charger?.ownerId)?.name || '';
  }, [charger?.ownerId, companies]);

  const renderDisplayId = () => (
    <div className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}>
      <Label
        text={t('chargers_display_id')}
        type={LabelType.LABEL_S}
        color={ColorType.GREY6}
        className='min-w-[148px] max-w-[148px] mr-2.5'
      />
      <div className='flex-1'>
        <InputDisplayOCPP
          keyId='displayId'
          charger={charger}
          defaultValue={charger?.name}
          setisValidDisplayIdOCPP={setisValidDisplayIdOCPP}
          handleInputChange={(value: string) => handleInputChange('displayId', value)}
        />
      </div>
    </div>
  );

  const renderMakeAndModel = () => {
    return editEnabled ? (
      <div className='flex flex-col gap-0'>
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <Label
            text={t('chargers_manufacturer')}
            type={LabelType.LABEL_S}
            color={ColorType.GREY6}
            className='min-w-[148px] max-w-[148px] mr-2.5'
          />
          {!charger?.isPowerManaged && (
            <div className='flex-1 min-w-0'>
              <Dropdown
                headerWidth='100%'
                items={manufacturerDropdown}
                onItemClick={(selectedItem: any) => {
                  setManufacturer(selectedItem.label);
                  handleInputChange('chargerModelId', '');
                }}
                labelType={LabelType.BODY3_G4}
                contentDivWidth={220}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
                headerClassName='bg-grey1 border-grey3 rounded pr-2'
                headerHeight='36px'
              />
            </div>
          )}
          {charger?.isPowerManaged && (
            <Label text={manufacturer} type={LabelType.BODY3} color={ColorType.BLACK} />
          )}
        </div>
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <Label
            text={t('chargers_model')}
            type={LabelType.LABEL_S}
            color={ColorType.GREY6}
            className='min-w-[148px] max-w-[148px] mr-2.5'
          />
          {!charger?.isPowerManaged && (
            <div className='flex-1 min-w-0'>
              <Dropdown
                headerWidth='100%'
                items={modelDropdown || []}
                disabled={!modelDropdown}
                onItemClick={(selectedItem: any) => {
                  handleInputChange('chargerModelId', selectedItem.id);
                }}
                labelType={LabelType.BODY3_G4}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
                headerClassName='bg-grey1 border-grey3 rounded pr-2'
                headerHeight='36px'
                contentDivWidth={220}
              />
            </div>
          )}
          {charger?.isPowerManaged && (
            <Label text={charger.model} type={LabelType.BODY3} color={ColorType.BLACK} />
          )}
        </div>
      </div>
    ) : (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <Label
          text={t('charger_make')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5'
        />
        <Label
          text={charger ? `${charger?.vendor} / ${charger?.model}` : ''}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          isLoading={isLoading}
          skeletonWidth='w-52'
          skeletonHeight='h-5'
        />
      </div>
    );
  };

  const renderMaintenanceMode = () => {
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <div className='flex flex-row gap-[4px] min-w-[148px] max-w-[148px] mr-2.5 items-center'>
          <Label text={t('maintenance_flag')} type={LabelType.LABEL_S} color={ColorType.GREY6} />
          <Tooltip>
            <TooltipTrigger>
              <Icon src={Info} size={IconSize.SIZE_20x20} />
            </TooltipTrigger>
            <TooltipContent>
              <span className='text-sm text-white font-medium' style={{ fontFamily: 'Inter' }}>
                <Trans
                  i18nKey='maintenance_flag_tip'
                  components={{
                    br: <br />,
                  }}
                />
              </span>
            </TooltipContent>
          </Tooltip>
        </div>
        {editEnabled ? (
          <Switch
            value={updateChargerDetail.maintenanceFlag}
            enableLabel={t('under_maintenance')}
            disableLabel={t('not_under_maintenance')}
            onChange={(value: any) => {
              setUpdateChargerDetail({
                ...updateChargerDetail,
                maintenanceFlag: value,
              });
            }}
            dataTestId='charger-maintenance-switch'
          />
        ) : (
          <Label
            text={t(
              updateChargerDetail.maintenanceFlag ? 'under_maintenance' : 'not_under_maintenance',
            )}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLoading}
            skeletonWidth='w-52'
            skeletonHeight='h-5'
          />
        )}
      </div>
    );
  };

  const renderCompanyName = () => {
    const companyId = charger?.ownerType === OWNER_TYPE.COMPANY ? charger?.ownerId : null;
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <Label
          text={t('company')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5'
        />
        <div className='flex flex-row gap-[8px]'>
          {companyId ? (
            <Link
              to={`/overview?companyId=${companyId}`}
              className={`${LabelType.BODY3} hover:opacity-80`}
              style={{ color: getHexColorByType(ColorType.DENIM) }}
            >
              {companyName}
            </Link>
          ) : (
            <Label
              text={companyName}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
              className='min-w-[148px] max-w-[148px] mr-2.5'
            />
          )}
        </div>
      </div>
    );
  };

  const renderLocation = () => {
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <Label
          text={t('charger_location')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5'
        />
        {!editEnabled && (
          <Label
            text={charger?.location?.name || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLoading}
            skeletonWidth='w-52'
            skeletonHeight='h-5'
          />
        )}
        {editEnabled && charger?.isPowerManaged && (
          <Label
            text={charger?.location?.name || ''}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
        )}
        {editEnabled && !charger?.isPowerManaged && (
          <div className='flex-1 min-w-0'>
            <Dropdown
              placeholder={t('chargers_location')}
              placeholderLabelType={LabelType.DROPDOWN_HEADER}
              headerWidth='100%'
              items={locationDropdown}
              onItemClick={(selectedItem: any) => {
                handleInputChange('locationId', selectedItem.id);
              }}
              labelType={LabelType.BODY3_G4}
              labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
              chevdownIconHighlightColor={ColorType.GREY6}
              headerHighLightClassName='bg-grey1 border-grey3 rounded h-[36px] pr-2'
              headerClassName='bg-grey1 border-grey3 rounded pr-2'
              headerHeight='36px'
              contentDivWidth={220}
            />
          </div>
        )}
      </div>
    );
  };

  const renderAddress = () => {
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <Label
          text={t('charger_address')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5'
        />
        <Label
          text={
            charger
              ? `${charger?.location?.streetAddress}, ${charger?.location?.city},
                  ${charger?.location?.stateOrRegion}, ${charger?.location?.isoCountryCode}, ${charger?.location?.zipOrPostalCode}`
              : ''
          }
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          className='w-52'
          isLoading={isLoading}
          skeletonWidth='w-52'
          skeletonHeight='h-5'
        />
      </div>
    );
  };

  const renderLastUsed = () => {
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <Label
          text={t('charger_last_used')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5 whitespace-nowrap'
        />
        <Label
          text={
            charger?.lastUsed && charger.location?.timeZone
              ? formatInTimeZone(
                  charger?.lastUsed,
                  charger.location.timeZone,
                  'LLL d, h:mm a',
                  getLocale(),
                ) + getISO8601TimezoneOffSetFromTimeZone(charger.location.timeZone)
              : ''
          }
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          isLoading={isLoading}
          skeletonWidth='w-52'
          skeletonHeight='h-5'
        />
      </div>
    );
  };

  const renderOpccId = () => {
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <div className='flex flex-row gap-[4px] min-w-[148px] max-w-[148px] mr-2.5 items-center'>
          <Label text={t('chargers_ocpp_id')} type={LabelType.LABEL_S} color={ColorType.GREY6} />
          <Tooltip>
            <TooltipTrigger>
              <Icon src={Info} size={IconSize.SIZE_20x20} />
            </TooltipTrigger>
            <TooltipContent>
              <Label text={t('charger_ocpp_tip')} type={LabelType.BODY3} color={ColorType.WHITE} />
            </TooltipContent>
          </Tooltip>
        </div>
        {!editEnabled && <CopyTooltip text={charger?.ocppId || ''} />}
        {editEnabled && (
          <div className='flex-1'>
            <InputDisplayOCPP
              keyId='ocppId'
              charger={charger}
              defaultValue={charger?.ocppId}
              setisValidDisplayIdOCPP={setisValidDisplayIdOCPP}
              handleInputChange={(value: string) => handleInputChange('ocppId', value)}
              maxLen={String(OCPP_ID_LENGTH)}
              inputType={InputType.TEXT}
            />
          </div>
        )}
      </div>
    );
  };

  const renderSerialNumber = () => {
    return (
      <div
        className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
      >
        <Label
          text={t('serial_number_label')}
          type={LabelType.LABEL_S}
          color={ColorType.GREY6}
          className='min-w-[148px] max-w-[148px] mr-2.5'
        />
        <Tooltip>
          <TooltipTrigger>
            <Label
              text={charger?.serialNumber || ''}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
              isLoading={isLoading}
              skeletonWidth='w-52'
              skeletonHeight='h-5'
              className='truncate max-w-[265px]'
            />
          </TooltipTrigger>
          <TooltipContent style={{ maxWidth: 'fit-content' }}>
            <Label
              text={charger?.serialNumber || ''}
              type={LabelType.BODY3}
              color={ColorType.WHITE}
            />
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const renderChargerStatus = () => {
    const { timeStamp, status, ports } = getRowStatus(charger);
    return (
      charger?.ports?.length > 1 && (
        <div
          className={`flex flex-row ${editEnabled ? 'items-center' : 'items-start'}  min-h-[40px]`}
        >
          <Label
            text={t('overview_charger_status')}
            type={LabelType.LABEL_S}
            color={ColorType.GREY6}
            className='min-w-[148px] max-w-[148px] mr-2.5'
          />
          <div className='flex flex-row gap-[8px]'>
            {charger && (
              <ChargerStatus
                status={status}
                ports={ports}
                timeStamp={timeStamp}
                isLoading={isLoading}
              />
            )}
            {updateChargerDetail.maintenanceFlag && (
              <Icon src={maintenanceIcon} size={IconSize.SIZE_20x20} />
            )}
          </div>
        </div>
      )
    );
  };

  return (
    <div className={`flex flex-col ${editEnabled ? 'gap-1' : 'gap-0'} pt-4`}>
      {editEnabled && renderDisplayId()}
      {renderMakeAndModel()}
      {!editEnabled && renderChargerStatus()}
      {renderMaintenanceMode()}
      {renderCompanyName()}
      {renderLocation()}
      {renderAddress()}
      {renderLastUsed()}
      {renderOpccId()}
      {renderSerialNumber()}
      {auth.role === USER_ROLE.SUPPORT && (
        <ChargersLicenseDetails isLoading={isLoading} license={license} />
      )}
    </div>
  );
};
